import React, { useRef } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslator } from '@ianesthesie-kit/core';
import { ConfirmDelete } from '@/components/ConfirmDelete';

export type ResetIconButtonProps = Pick<IconButtonProps, 'disabled'> & {
  label: string;
  loading?: boolean;
  onClick: () => void;
};

export const ResetIconButton = ({
  label,
  loading,
  disabled,
  onClick
}: ResetIconButtonProps) => {
  const translate = useTranslator();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cancelLabel = translate('app.cancel');
  const confirmLabel = translate(
    'doctor-dashboard.details.reset.confirm',
    undefined,
    { action: label }
  );

  return (
    <ConfirmDelete onClick={onClick}>
      {({ confirm, onClick: onResetClick, onCancel }) => (
        <Box
          component="span"
          sx={{ display: 'inline-flex', flexShrink: 0 }}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              onCancel();
            }
          }}
          onKeyDown={(event) => {
            if (confirm && event.key === 'Escape') {
              event.stopPropagation();
              onCancel();
              buttonRef.current?.focus();
            }
          }}
        >
          <Tooltip title={confirm ? confirmLabel : label} disableInteractive>
            <span>
              <IconButton
                ref={buttonRef}
                size="small"
                disabled={disabled || loading}
                aria-label={confirm ? confirmLabel : label}
                aria-busy={loading}
                onClick={onResetClick}
                sx={{
                  background: 'transparent!important',
                  color: confirm ? 'error.main' : undefined,
                  '&:hover': { color: 'error.main' },
                  '& > svg': { opacity: 0.85, '&:hover': { opacity: 1 } }
                }}
              >
                {loading ? (
                  <CircularProgress size={20} thickness={4} color="inherit" />
                ) : confirm ? (
                  <CheckIcon sx={{ width: 20, height: 20 }} />
                ) : (
                  <RestartAltIcon
                    sx={{
                      width: 20,
                      height: 20,
                      // Match Copy's visible glyph size without enlarging the button.
                      transform: 'scale(1.16)'
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
          {confirm && (
            <Tooltip title={cancelLabel} disableInteractive>
              <IconButton
                size="small"
                aria-label={cancelLabel}
                disabled={disabled || loading}
                onClick={() => {
                  onCancel();
                  buttonRef.current?.focus();
                }}
                sx={{
                  background: 'transparent!important',
                  '& > svg': { opacity: 0.85, '&:hover': { opacity: 1 } }
                }}
              >
                <BlockIcon sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </ConfirmDelete>
  );
};
